import { useState, useEffect } from "react";
import { api, formatApiError, imgUrl } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit3, X } from "lucide-react";
import { toast } from "sonner";
import { Field } from "./shared";

export function ChroniclesPanel() {
  const blank = {
    title: "",
    episode_number: 1,
    subtitle: "",
    tagline: "",
    content: "",
    status: "published",
    scheduled_local: "",
    poll_question: "",
    poll_options_text: "",
  };
  const [episodes, setEpisodes] = useState([]);
  const [form, setForm] = useState(blank);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [gateLimit, setGateLimit] = useState(5);
  const [gateInput, setGateInput] = useState(5);
  const [gateBusy, setGateBusy] = useState(false);

  const load = async () => {
    try {
      const [eps, settings] = await Promise.all([
        api.get("/admin/chronicles"),
        api.get("/admin/chronicles/settings"),
      ]);
      setEpisodes(eps.data.episodes || []);
      const lim = settings.data.free_episode_limit ?? 5;
      setGateLimit(lim);
      setGateInput(lim);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load chronicles");
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, []);

  const saveGate = async () => {
    setGateBusy(true);
    try {
      const { data } = await api.put("/admin/chronicles/settings", {
        free_episode_limit: Math.max(0, parseInt(gateInput, 10) || 0),
      });
      setGateLimit(data.free_episode_limit);
      setGateInput(data.free_episode_limit);
      toast.success(`Free episodes set to ${data.free_episode_limit}. Episode ${data.free_episode_limit + 1}+ now requires sign-up.`);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not save gate setting");
    } finally {
      setGateBusy(false);
    }
  };

  const reset = () => {
    setForm(blank);
    setEditingId(null);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let scheduled_for = null;
      if (form.status === "scheduled") {
        if (!form.scheduled_local) {
          toast.error("Pick a date & time to schedule this episode.");
          setBusy(false);
          return;
        }
        scheduled_for = new Date(form.scheduled_local).toISOString();
      }
      const payload = {
        title: form.title,
        episode_number: Math.max(1, parseInt(form.episode_number, 10) || 1),
        subtitle: form.subtitle || null,
        tagline: form.tagline || null,
        content: form.content,
        status: form.status,
        scheduled_for,
        poll_question: form.poll_question || null,
        poll_options: (form.poll_options_text || "")
          .split("\n")
          .map((o) => o.trim())
          .filter(Boolean),
      };
      if (editingId) {
        await api.put(`/admin/chronicles/${editingId}`, payload);
        toast.success("Episode updated.");
        reset();
        await load();
      } else {
        const { data } = await api.post("/admin/chronicles", payload);
        toast.success(`Episode created. Now upload the cover + graphics →`);
        setEditingId(data.id);
        await load();
      }
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete "${title}"? This removes the episode and its images.`)) return;
    try {
      await api.delete(`/admin/chronicles/${id}`);
      toast.success("Deleted.");
      if (editingId === id) reset();
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const isoToLocalInput = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const off = d.getTimezoneOffset();
    const local = new Date(d.getTime() - off * 60000);
    return local.toISOString().slice(0, 16);
  };

  const startEdit = (ep) => {
    setEditingId(ep.id);
    setForm({
      title: ep.title || "",
      episode_number: ep.episode_number || 1,
      subtitle: ep.subtitle || "",
      tagline: ep.tagline || "",
      content: ep.content || "",
      status: ep.status || (ep.published ? "published" : "draft"),
      scheduled_local: isoToLocalInput(ep.scheduled_for),
      poll_question: ep.poll_question || "",
      poll_options_text: (ep.poll_options || []).join("\n"),
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const editingEp = episodes.find((e) => e.id === editingId);

  return (
    <div>
      <div className="surface p-5 mb-6 flex items-center gap-4 flex-wrap" data-testid="chronicle-gate-setting">
        <div className="flex-1 min-w-[220px]">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold)] mb-1">Sign-up gate</p>
          <p className="text-xs text-white/55">
            Episodes 1–{gateLimit} are free. Episode {gateLimit + 1}+ requires a reader to sign up (email) to read.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="eyebrow">Free episodes</Label>
            <Input
              type="number"
              min={0}
              value={gateInput}
              onChange={(e) => setGateInput(e.target.value)}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-10 w-24 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="chronicle-gate-input"
            />
          </div>
          <Button
            type="button"
            onClick={saveGate}
            disabled={gateBusy || parseInt(gateInput, 10) === gateLimit}
            className="bg-transparent border border-[color:var(--gold)]/50 text-[color:var(--gold)] rounded-none px-5 h-10 text-xs hover:bg-[color:var(--gold)]/10 disabled:opacity-40"
            data-testid="chronicle-gate-save"
          >
            {gateBusy ? "Saving…" : "Save gate"}
          </Button>
        </div>
      </div>

      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <p className="text-sm text-white/55">
          {episodes.length} episode{episodes.length === 1 ? "" : "s"} · The Mob Mistress Chronicles
        </p>
        <Button
          onClick={() => {
            if (showForm) reset();
            else {
              setForm({ ...blank, episode_number: (episodes[episodes.length - 1]?.episode_number || 0) + 1 });
              setEditingId(null);
              setShowForm(true);
            }
          }}
          className="btn-gold rounded-none px-6 py-4 text-xs"
          data-testid="admin-new-chronicle"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />}
          {showForm ? "Close" : "New Episode"}
        </Button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="surface p-8 mb-10 grid sm:grid-cols-2 gap-6" data-testid="chronicle-form">
          <Field label="Episode Title" full>
            <Input
              required
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="chronicle-title"
            />
          </Field>
          <Field label="Episode Number">
            <Input
              type="number"
              min={1}
              required
              value={form.episode_number}
              onChange={(e) => setForm({ ...form, episode_number: e.target.value })}
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="chronicle-number"
            />
          </Field>
          <Field label="Status">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="bg-transparent border-b border-white/15 h-11 outline-none w-full text-sm"
              data-testid="chronicle-status"
            >
              <option value="published" style={{ background: "#1a0d12" }}>Publish now (live)</option>
              <option value="scheduled" style={{ background: "#1a0d12" }}>Schedule for later</option>
              <option value="draft" style={{ background: "#1a0d12" }}>Draft (hidden)</option>
            </select>
          </Field>
          {form.status === "scheduled" && (
            <Field label="Drop date & time (your local time — auto-publishes + emails the list)" full>
              <Input
                type="datetime-local"
                value={form.scheduled_local}
                onChange={(e) => setForm({ ...form, scheduled_local: e.target.value })}
                className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)] [color-scheme:dark]"
                data-testid="chronicle-scheduled"
              />
            </Field>
          )}
          <Field label="Subtitle (optional)" full>
            <Input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              placeholder="Mob Mistress Entertainment — The Origin"
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="chronicle-subtitle"
            />
          </Field>
          <Field label="Tagline / hook (optional — shown big at the top)" full>
            <textarea
              rows={2}
              value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              maxLength={400}
              className="w-full bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] p-3 text-sm resize-none outline-none"
              data-testid="chronicle-tagline"
            />
          </Field>
          <Field label="Story Body — separate paragraphs with a blank line. Use **text** for gold emphasis." full>
            <textarea
              rows={12}
              required
              value={form.content}
              onChange={(e) => setForm({ ...form, content: e.target.value })}
              className="w-full bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] p-3 text-sm resize-y outline-none font-mono leading-relaxed"
              data-testid="chronicle-content"
            />
          </Field>

          <Field label="Reader Poll — question (optional · 'What should Nina do next?')" full>
            <Input
              value={form.poll_question}
              onChange={(e) => setForm({ ...form, poll_question: e.target.value })}
              placeholder="What should Nina do next?"
              className="bg-transparent border-0 border-b border-white/15 rounded-none px-0 h-11 focus-visible:ring-0 focus-visible:border-[color:var(--gold)]"
              data-testid="chronicle-poll-question"
            />
          </Field>
          <Field label="Poll options — one per line (leave blank for no poll · max 6)" full>
            <textarea
              rows={4}
              value={form.poll_options_text}
              onChange={(e) => setForm({ ...form, poll_options_text: e.target.value })}
              placeholder={"Confront the wife\nPlay the long game ♟️\nWalk away clean\nExpose everything"}
              className="w-full bg-transparent border border-white/10 rounded-none focus-visible:ring-0 focus-visible:border-[color:var(--gold)] p-3 text-sm resize-none outline-none"
              data-testid="chronicle-poll-options"
            />
          </Field>

          {editingId && (
            <div className="sm:col-span-2 grid sm:grid-cols-2 gap-8 border-t border-white/5 pt-6">
              <ChronicleCoverUploader episodeId={editingId} currentCover={editingEp?.cover_image} onUploaded={load} />
              <ChronicleImagesManager episodeId={editingId} images={editingEp?.images || []} onChanged={load} />
            </div>
          )}

          <div className="sm:col-span-2 flex gap-4 pt-2 border-t border-white/5">
            <Button type="submit" disabled={busy} className="btn-gold rounded-none px-7 py-3 text-xs" data-testid="chronicle-submit">
              {busy ? "Saving…" : editingId ? "Save Changes" : "Create Episode"}
            </Button>
            {editingId && (
              <Button type="button" onClick={reset} className="bg-transparent border border-white/15 rounded-none px-5 py-3 text-xs hover:border-white/40">
                Done
              </Button>
            )}
          </div>
        </form>
      )}

      <div className="space-y-3" data-testid="chronicle-list">
        {episodes.length === 0 ? (
          <p className="text-sm text-white/50">No episodes yet. Create one and upload the cover + graphics.</p>
        ) : (
          episodes.map((ep) => (
            <div key={ep.id} className="surface p-5 flex items-center gap-4 flex-wrap" data-testid={`chronicle-row-${ep.slug}`}>
              <div
                className="w-16 h-16 bg-cover bg-center shrink-0 border border-white/10"
                style={{ backgroundImage: ep.cover_image ? `url(${imgUrl(ep.cover_image)})` : "linear-gradient(135deg, #2a1822, #1a0d12)" }}
              />
              <div className="flex-1 min-w-[200px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong style={{ color: "var(--gold)" }}>Ep {ep.episode_number} · {ep.title}</strong>
                  {(() => {
                    const st = ep.status || (ep.published ? "published" : "draft");
                    const map = {
                      published: { label: "live", color: "#9ddf8b", border: "rgba(157,223,139,0.4)" },
                      scheduled: { label: "scheduled", color: "#e8b4b8", border: "rgba(232,180,184,0.45)" },
                      draft: { label: "draft", color: "rgba(255,255,255,0.4)", border: "rgba(255,255,255,0.15)" },
                    };
                    const s = map[st] || map.draft;
                    return (
                      <span
                        className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border"
                        style={{ color: s.color, borderColor: s.border }}
                        data-testid={`chronicle-status-${ep.slug}`}
                      >
                        {s.label}
                      </span>
                    );
                  })()}
                  {ep.episode_number > gateLimit && (
                    <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-[color:var(--gold)]/40 text-[color:var(--gold)]">
                      🔒 gated
                    </span>
                  )}
                  {!ep.cover_image && (
                    <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border border-red-400/40 text-red-300/80">
                      No cover
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-white/45 mt-1">
                  {(ep.images?.length || 0)} graphic{(ep.images?.length || 0) === 1 ? "" : "s"} · {ep.slug}
                  {ep.status === "scheduled" && ep.scheduled_for && (
                    <span className="text-[color:var(--gold)]/80">
                      {" "}· drops {new Date(ep.scheduled_for).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <a
                  href={`/chronicles/${ep.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[10px] uppercase tracking-[0.22em] text-white/55 hover:text-[color:var(--gold)]"
                  data-testid={`chronicle-view-${ep.slug}`}
                >
                  View
                </a>
                <button onClick={() => startEdit(ep)} className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] hover:opacity-80" data-testid={`chronicle-edit-${ep.slug}`}>
                  <Edit3 size={11} className="inline mr-1" />
                  Edit
                </button>
                <button onClick={() => remove(ep.id, ep.title)} className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-400" data-testid={`chronicle-delete-${ep.slug}`}>
                  <Trash2 size={11} className="inline mr-1" />
                  Del
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <ChronicleCommentsModeration />
    </div>
  );
}

// ---------------------------------------------------------------------
// Chronicle Comments Moderation — approve / reply / reject reader comments
// ---------------------------------------------------------------------
function ChronicleCommentsModeration() {
  const [comments, setComments] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [filter, setFilter] = useState("pending");
  const [replyFor, setReplyFor] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [readers, setReaders] = useState(0);

  const load = async () => {
    try {
      const [c, r] = await Promise.all([
        api.get(`/admin/chronicles/comments${filter !== "all" ? `?status=${filter}` : ""}`),
        api.get("/admin/chronicles/readers"),
      ]);
      setComments(c.data.comments || []);
      setPendingCount(c.data.pending_count || 0);
      setReaders(r.data.count || 0);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not load comments");
    }
  };
  useEffect(() => {
    const id = setTimeout(load, 0);
    return () => clearTimeout(id);
  }, [filter]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/chronicles/comments/${id}/${action}`);
      toast.success(action === "approve" ? "Comment approved." : "Comment rejected.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Action failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this comment permanently?")) return;
    try {
      await api.delete(`/admin/chronicles/comments/${id}`);
      toast.success("Deleted.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/admin/chronicles/comments/${id}/reply`, { reply: replyText.trim() });
      toast.success("Reply sent + comment approved. The reader was emailed.");
      setReplyFor(null);
      setReplyText("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Reply failed");
    }
  };

  const badge = (st) => {
    const map = {
      pending: { c: "#e8b4b8", b: "rgba(232,180,184,0.45)" },
      approved: { c: "#9ddf8b", b: "rgba(157,223,139,0.4)" },
      rejected: { c: "rgba(255,255,255,0.4)", b: "rgba(255,255,255,0.15)" },
    };
    const s = map[st] || map.rejected;
    return (
      <span className="text-[9px] uppercase tracking-[0.22em] px-2 py-0.5 border" style={{ color: s.c, borderColor: s.b }}>
        {st}
      </span>
    );
  };

  return (
    <div className="mt-12 border-t border-white/10 pt-10" data-testid="chronicle-comments-moderation">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.28em] text-[color:var(--gold)] mb-1">Reader Comments</p>
          <p className="text-xs text-white/55">
            {pendingCount} pending · {readers} engaged reader{readers === 1 ? "" : "s"} captured
          </p>
        </div>
        <div className="flex gap-2">
          {["pending", "approved", "all"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`text-[10px] uppercase tracking-[0.22em] px-3 py-2 border rounded-none ${
                filter === f
                  ? "border-[color:var(--gold)] text-[color:var(--gold)]"
                  : "border-white/15 text-white/55 hover:border-white/40"
              }`}
              data-testid={`chronicle-comments-filter-${f}`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      {comments.length === 0 ? (
        <p className="text-sm text-white/45 py-4">No {filter === "all" ? "" : filter} comments.</p>
      ) : (
        <div className="space-y-3">
          {comments.map((c) => (
            <div key={c.id} className="surface p-5" data-testid={`chronicle-comment-row-${c.id}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <strong style={{ color: "var(--gold)" }}>{c.name}</strong>
                  {badge(c.status)}
                  <span className="text-[11px] text-white/40">{c.email}</span>
                  <span className="text-[11px] text-white/35">· Ep {c.episode_title || c.slug}</span>
                </div>
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/30">
                  {(c.created_at || "").slice(0, 10)}
                </span>
              </div>
              <p className="text-sm text-white/75 leading-relaxed whitespace-pre-line">{c.body}</p>
              {c.admin_reply && (
                <div className="mt-3 pl-3 border-l-2 border-[color:var(--gold)]/50">
                  <p className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] mb-1">Your reply</p>
                  <p className="text-sm text-white/65 whitespace-pre-line">{c.admin_reply}</p>
                </div>
              )}

              {replyFor === c.id ? (
                <div className="mt-4 space-y-2">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    rows={2}
                    placeholder="Write a reply — the reader gets emailed and the comment goes live."
                    className="w-full bg-transparent border border-white/15 rounded-none p-3 text-sm outline-none focus:border-[color:var(--gold)] resize-none"
                    data-testid={`chronicle-reply-input-${c.id}`}
                  />
                  <div className="flex gap-2">
                    <Button onClick={() => sendReply(c.id)} className="btn-gold rounded-none px-4 py-2 text-[11px]" data-testid={`chronicle-reply-send-${c.id}`}>
                      Send Reply
                    </Button>
                    <Button onClick={() => { setReplyFor(null); setReplyText(""); }} className="bg-transparent border border-white/15 rounded-none px-4 py-2 text-[11px] hover:border-white/40">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  {c.status !== "approved" && (
                    <button onClick={() => act(c.id, "approve")} className="text-[10px] uppercase tracking-[0.22em] text-[#9ddf8b] hover:opacity-80" data-testid={`chronicle-comment-approve-${c.id}`}>
                      ✓ Approve
                    </button>
                  )}
                  <button onClick={() => { setReplyFor(c.id); setReplyText(c.admin_reply || ""); }} className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--gold)] hover:opacity-80" data-testid={`chronicle-comment-reply-${c.id}`}>
                    Reply
                  </button>
                  {c.status !== "rejected" && (
                    <button onClick={() => act(c.id, "reject")} className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-white" data-testid={`chronicle-comment-reject-${c.id}`}>
                      Reject
                    </button>
                  )}
                  <button onClick={() => remove(c.id)} className="text-[10px] uppercase tracking-[0.22em] text-white/45 hover:text-red-400" data-testid={`chronicle-comment-delete-${c.id}`}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChronicleCoverUploader({ episodeId, currentCover, onUploaded }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/chronicles/${episodeId}/cover`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Cover uploaded.");
      if (onUploaded) await onUploaded();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Cover upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Cover Image (JPG / PNG / WebP · max 8 MB)</Label>
      <div className="flex items-center gap-3">
        {currentCover && (
          <img src={imgUrl(currentCover)} alt="Cover" className="w-14 h-14 object-cover border border-white/10" data-testid="chronicle-cover-current" />
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp"
          disabled={busy}
          onChange={(e) => upload(e.target.files?.[0])}
          className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
          data-testid="chronicle-cover-upload"
        />
      </div>
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}

function ChronicleImagesManager({ episodeId, images, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/chronicles/${episodeId}/images`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Graphic added.");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  const removeImg = async (url) => {
    try {
      await api.post(`/admin/chronicles/${episodeId}/images/remove`, { url });
      toast.success("Graphic removed.");
      if (onChanged) await onChanged();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Remove failed");
    }
  };
  return (
    <div className="space-y-2">
      <Label className="eyebrow">Story Graphics (interleaved through the text · in order)</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        {(images || []).map((url, i) => (
          <div key={url} className="relative group" data-testid={`chronicle-image-${i}`}>
            <img src={imgUrl(url)} alt={`Graphic ${i + 1}`} className="w-16 h-16 object-cover border border-white/10" />
            <button
              type="button"
              onClick={() => removeImg(url)}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black/80 border border-red-400/50 text-red-300 text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`chronicle-image-remove-${i}`}
              title="Remove"
            >
              <X size={11} />
            </button>
          </div>
        ))}
      </div>
      <input
        type="file"
        accept="image/jpeg,image/png,image/webp"
        disabled={busy}
        onChange={(e) => upload(e.target.files?.[0])}
        className="text-xs text-white/65 file:mr-3 file:py-2 file:px-4 file:border file:border-[color:var(--gold)]/50 file:bg-transparent file:text-[color:var(--gold)] file:text-[10px] file:uppercase file:tracking-[0.22em] file:rounded-none file:cursor-pointer hover:file:bg-[color:var(--gold)]/10"
        data-testid="chronicle-image-upload"
      />
      {busy && <p className="text-[10px] uppercase tracking-[0.22em] text-white/55">Uploading…</p>}
    </div>
  );
}
